import { useNavigate } from "react-router-dom";
import { ApplicationRoutes } from "../../../../../routes";
import styles from "./AllServiceRequestInstancesPage.module.scss";
import DataTableV2, { ColumnTypeV2 } from "../../../../../common/components/xepp/table-v2/DataTableV2";
import EmptyState from "../../../../../common/components/xepp/empty-state/EmptyState";
import { DownArrowIcon, FilterIconV2, HeadphoneIcon, MobileIcon, ServiceRequestsIcon } from "../../../../../assets/icons";
import { ServiceRequestInstanceDetailsResponse, SlaStatus } from "../../../../../model/service-request/service-request-instance-details";
import { TablePaginationV2 } from "../../../../../common/components/xepp/table-pagination-v2/TablePaginationV2";
import Button from "../../../../../common/components/xepp/button/Button";
import SearchInput from "../../../../../common/components/xepp/search-input/SearchInput";
import { useEffect, useState } from "react";
import { Range } from "react-date-range";
import { useGetAllRequestTypesQuery, useLazyGetPageOfAllServiceRequestInstancesQuery, useLazyGetServiceRequestInstanceDetailsByIdQuery } from "../../../../../service/customersupport-service";
import CommonSideDialog from "../../../../../common/components/side-dialog/CommonSideDialog";
import ServiceRequestInstanceDetailsPanel from "./service-request-instance-details/ServiceRequestInstanceDetailsPanel";
import { useGetDepartmentsQuery } from "../../../../../service/department-service";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { formatTimestamp } from "../../../../../utils";
import FilterV2 from "../../../../../common/components/xepp/table-filter-v2/FilterV2";
import { FilterV2Category } from "../../../../../common/components/xepp/table-filter-v2/filter-v2.types";
import StatusBadge, { StatusBadgeVariant } from "../../../../../common/components/xepp/status-badge/StatusBadge";

const slaStatusVariant: Record<SlaStatus, StatusBadgeVariant> = {
	[SlaStatus.OnTrack]: "onTrack",
	[SlaStatus.AtRisk]: "atRisk",
	[SlaStatus.Breached]: "breached",
	[SlaStatus.Completed]: "completed",
};

const slaStatusLabel: Record<SlaStatus, string> = {
	[SlaStatus.OnTrack]: "On Track",
	[SlaStatus.AtRisk]: "At Risk",
	[SlaStatus.Breached]: "Breached",
	[SlaStatus.Completed]: "Completed",
};

const AllServiceRequestInstancesPage = () => {
	const navigator = useNavigate();

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);

	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [selectedSlaStatuses, setSelectedSlaStatuses] = useState<string[]>([]);
	const [selectedSubmissionSource, setSelectedSubmissionSource] = useState<string>("");
	const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
	const [selectedRequestTypeIds, setSelectedRequestTypeIds] = useState<string[]>([]);
	const [dateRange, setDateRange] = useState<Range | null>(null);
	const [appliedFilters, setAppliedFilters] = useState<{
		from?: string;
		to?: string;
		slaStatuses?: number[];
		departmentIds?: string[];
		requestTypeIds?: string[];
		isSubmittedBySupportAgent?: boolean;
	}>({});

	const { data: departmentsData } = useGetDepartmentsQuery();
	const { data: requestTypesData } = useGetAllRequestTypesQuery();

	const [getServiceRequestInstances, { data, isFetching, isError }] = useLazyGetPageOfAllServiceRequestInstancesQuery();
	const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
	const [getInstanceDetails, { data: instanceDetails }] = useLazyGetServiceRequestInstanceDetailsByIdQuery();

	const { count } = data ?? {};

	useEffect(() => {
		getServiceRequestInstances({
			page,
			pageSize,
			...(searchTerm && { search: searchTerm }),
			...(appliedFilters.slaStatuses?.length && { slaStatuses: appliedFilters.slaStatuses }),
			...(appliedFilters.from && { from: appliedFilters.from }),
			...(appliedFilters.to && { to: appliedFilters.to }),
			...(appliedFilters.departmentIds?.length && { departmentIds: appliedFilters.departmentIds }),
			...(appliedFilters.requestTypeIds?.length && { requestTypeIds: appliedFilters.requestTypeIds }),
			...(appliedFilters.isSubmittedBySupportAgent !== undefined && { isSubmittedBySupportAgent: appliedFilters.isSubmittedBySupportAgent }),
		})
			.unwrap()
			.catch(err => getErrorMessage(err.data));
	}, [page, pageSize, searchTerm, appliedFilters, getServiceRequestInstances]);

	const handleApplyFilter = () => {
		setAppliedFilters({
			slaStatuses: selectedSlaStatuses.length ? selectedSlaStatuses.map(Number) : undefined,
			from: dateRange?.startDate ? dateRange.startDate.toISOString() : undefined,
			to: dateRange?.endDate ? new Date(new Date(dateRange.endDate).setHours(23, 59, 59, 999)).toISOString() : undefined,
			departmentIds: selectedDepartmentIds.length ? selectedDepartmentIds : undefined,
			requestTypeIds: selectedRequestTypeIds.length ? selectedRequestTypeIds : undefined,
			isSubmittedBySupportAgent: selectedSubmissionSource !== "" ? selectedSubmissionSource === "true" : undefined,
		});
		setIsFilterOpen(false);
		setPage(1);
	};

	const handleResetFilter = () => {
		setSelectedSlaStatuses([]);
		setSelectedSubmissionSource("");
		setSelectedDepartmentIds([]);
		setSelectedRequestTypeIds([]);
		setDateRange(null);
		setAppliedFilters({});
		setIsFilterOpen(false);
		setPage(1);
	};

	const categories: FilterV2Category[] = [
		{
			id: "department",
			label: "Department",
			type: "checkbox",
			options: (departmentsData ?? []).map(d => ({
				id: d.id,
				label: d.name,
				value: d.id,
				isSelected: selectedDepartmentIds.includes(d.id),
			})),
			onChange: (values) => setSelectedDepartmentIds(values),
		},
		{
			id: "request_type",
			label: "Request Type",
			type: "checkbox",
			options: (requestTypesData ?? []).map(rt => ({
				id: rt.id,
				label: rt.name,
				value: rt.id,
				isSelected: selectedRequestTypeIds.includes(rt.id),
			})),
			onChange: (values) => setSelectedRequestTypeIds(values),
		},
		{
			id: "date_time",
			label: "Date & Time",
			type: "date",
			initialDateRange: dateRange ?? undefined,
			onChange: (range: any) => setDateRange(range.selection ?? range),
			onClear: () => setDateRange(null),
		},
		{
			id: "submission_source",
			label: "Submission Source",
			type: "radio",
			options: [
				{ id: "customer_app", label: "Customer App", value: "false", isSelected: selectedSubmissionSource === "false" },
				{ id: "support_agent", label: "Support Agent", value: "true", isSelected: selectedSubmissionSource === "true" },
			],
			onChange: (value) => setSelectedSubmissionSource(prev => prev === value ? "" : value),
		},
		{
			id: "sla_status",
			label: "SLA Status",
			type: "checkbox",
			options: (Object.entries(slaStatusLabel) as [string, string][]).map(([key, label]) => ({
				id: `sla_${key}`,
				label,
				value: key,
				isSelected: selectedSlaStatuses.includes(key),
			})),
			onChange: (values) => setSelectedSlaStatuses(values),
		},
	];

	const columns: Array<ColumnTypeV2<ServiceRequestInstanceDetailsResponse>> = [
		{
			header: "Ticket Number & Date",
			accessor: "ticketNumberFormatted",
			width: "13%",
			render: (record) => (
				<div className={styles.multiLineCell}>
					<span className={styles.cellPrimary} title={record.ticketNumberFormatted}>{record.ticketNumberFormatted}</span>
					<span className={styles.cellSecondary} title={formatTimestamp(record.createdAt)}>{formatTimestamp(record.createdAt)}</span>
				</div>
			),
		},
		{
			header: "Customer Name & Meter Number",
			accessor: "customerData",
			width: "15%",
			render: (record) => {
				const fullName = `${record.customerData?.firstName ?? ""} ${record.customerData?.lastName ?? ""}`.trim();
				return (
					<div className={styles.multiLineCell}>
						<span className={styles.cellPrimary} title={fullName}>{fullName}</span>
						<span className={styles.cellSecondary} title={record.customerData?.meterNumber}>{record.customerData?.meterNumber}</span>
					</div>
				);
			},
		},
		{
			header: "Request Type & Created By",
			accessor: "requestType",
			width: "19%",
			render: (record) => {
				const createdBy = `${record.submitterData?.firstName ?? ""} ${record.submitterData?.lastName ?? ""}`.trim();
				return (
					<div className={styles.multiLineCell}>
						<span className={styles.cellPrimary} title={record.requestType?.name}>{record.requestType?.name}</span>
						<span className={styles.cellSecondary} title={createdBy}>{createdBy}</span>
					</div>
				);
			},
		},
		{
			header: "Submission Source",
			accessor: "agentId",
			width: "14%",
			render: (record) => {
				const isCustomer = record.agentId == null;
				return (
					<div className={styles.sourceBadge}>
						{isCustomer ? <MobileIcon /> : <HeadphoneIcon />}
						<span>{isCustomer ? "Customer App" : "Support Agent"}</span>
					</div>
				);
			},
		},
		{
			header: "Department",
			accessor: "currentStepData",
			width: "17%",
			render: (record) => {
				const name = record.currentStepData?.departmentName;
				return (
					<div className={styles.departmentCell}>
						<span title={name}>{name || "N/A"}</span>
						{name && <DownArrowIcon className={styles.arrowRight} />}
					</div>
				);
			},
		},
		{
			header: "SLA Status",
			accessor: "slaStatus",
			width: "10%",
			render: (record) => {
				const slaStatus = record.currentStepData?.slaStatus;
				if (slaStatus === undefined) return <span>N/A</span>;
				return (
					<StatusBadge variant={slaStatusVariant[slaStatus]}>
						{slaStatusLabel[slaStatus]}
					</StatusBadge>
				);
			},
		},
		{
			header: "Expected Completion Date",
			accessor: "due",
			width: "12%",
			render: (record) => {
				const isCompleted = record.currentStepData?.slaStatus === SlaStatus.Completed;
				const date = isCompleted
					? record.currentStepData?.updatedAt
					: record.currentStepData?.due;
				return <span>{date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "N/A"}</span>;
			},
		},
	];

	return (
		<div className={styles.pageContainer}>
			<div className={styles.pageHeader}>
				<h2 className={styles.pageTitle}>Service Request</h2>
				<Button
					text="Create Service Request"
					variant="primary"
					className={styles.createButton}
					onClick={() => navigator(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.CUSTOMER_SUPPORT}/${ApplicationRoutes.REPORT_SERVICE_REQUEST}`)}
				/>
			</div>

			<div className={styles.toolbar}>
				<div className={styles.filterWrapper}>
					<Button
						leftIcon={<FilterIconV2 />}
						text="Filter"
						variant="outline"
						className={styles.filterButton}
						onClick={() => setIsFilterOpen(true)}
					/>
					<FilterV2
						categories={categories}
						onApplyFilter={handleApplyFilter}
						onResetFilter={handleResetFilter}
						onClose={() => setIsFilterOpen(false)}
						isOpen={isFilterOpen}
					/>
				</div>
				<SearchInput
					placeholder="Ticket No, Site, Meter No"
					onSearch={(value) => { setSearchTerm(value); setPage(1); }}
					debounceTime={500}
					className={styles.searchInput}
				/>
			</div>

			<div className={styles.tableSection}>
				<DataTableV2<ServiceRequestInstanceDetailsResponse>
					data={data?.items ?? []}
					loading={isFetching}
					columns={columns}
					emptyComponent={<EmptyState icon={<ServiceRequestsIcon />} title="No service request submissions" />}
					onRowClick={(record) => {
						setSelectedInstanceId(record.id);
						getInstanceDetails(record.id);
					}}
				/>
			</div>

			<div className={styles.cardList}>
				{isFetching ? (
					<div className={styles.cardStateContainer}>
						<div className={styles.cardLoading} />
						<span className={styles.cardStateText}>Loading...</span>
					</div>
				) : isError ? (
					<div className={styles.cardStateContainer}>
						<div className={styles.cardErrorIcon}>!</div>
						<span className={styles.cardStateTitle}>Something went wrong</span>
						<span className={styles.cardStateText}>Failed to load service requests. Please try again.</span>
					</div>
				) : (data?.items ?? []).length === 0 ? (
					<EmptyState icon={<ServiceRequestsIcon />} title="No service request submissions" />
				) : (data?.items ?? []).map((record) => {
					const fullName = `${record.customerData?.firstName ?? ""} ${record.customerData?.lastName ?? ""}`.trim();
					const isCustomer = record.agentId == null;
					const slaStatus = record.currentStepData?.slaStatus;
					const isCompleted = slaStatus === SlaStatus.Completed;
					const completionDate = isCompleted
						? record.currentStepData?.updatedAt
						: record.currentStepData?.due;

					return (
						<div
							key={record.id}
							className={styles.card}
							onClick={() => { setSelectedInstanceId(record.id); getInstanceDetails(record.id); }}
						>
							<div className={styles.cardHeader}>
								<div className={styles.cardTicket}>
									<span className={styles.cardTicketNumber}>{record.ticketNumberFormatted}</span>
									<span className={styles.cardDate}>{formatTimestamp(record.createdAt)}</span>
								</div>
								{slaStatus !== undefined && (
									<StatusBadge variant={slaStatusVariant[slaStatus]}>
										{slaStatusLabel[slaStatus]}
									</StatusBadge>
								)}
							</div>

							<div className={styles.cardBody}>
								<div className={styles.cardRow}>
									<span className={styles.cardLabel}>Customer</span>
									<span className={styles.cardValue}>{fullName || "N/A"}</span>
								</div>
								<div className={styles.cardRow}>
									<span className={styles.cardLabel}>Meter No</span>
									<span className={styles.cardValue}>{record.customerData?.meterNumber || "N/A"}</span>
								</div>
								<div className={styles.cardRow}>
									<span className={styles.cardLabel}>Request Type</span>
									<span className={styles.cardValue}>{record.requestType?.name || "N/A"}</span>
								</div>
								<div className={styles.cardRow}>
									<span className={styles.cardLabel}>Department</span>
									<span className={styles.cardValue}>{record.currentStepData?.departmentName || "N/A"}</span>
								</div>
							</div>

							<div className={styles.cardFooter}>
								<div className={styles.sourceBadge}>
									{isCustomer ? <MobileIcon /> : <HeadphoneIcon />}
									<span>{isCustomer ? "Customer App" : "Support Agent"}</span>
								</div>
								{completionDate && (
									<span className={styles.cardDate}>
										{new Date(completionDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
									</span>
								)}
							</div>
						</div>
					);
				})}
			</div>

			<div className={styles.paginationRow}>
				<TablePaginationV2
					isLoading={isFetching}
					totalItems={count ?? 0}
					itemsPerPage={pageSize}
					currentPage={page}
					pageSizeOptions={[10, 20, 50]}
					onPageChange={setPage}
					onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
				/>
			</div>
			{selectedInstanceId && instanceDetails && (
				<CommonSideDialog onCloseDialog={() => setSelectedInstanceId(null)}>
					<ServiceRequestInstanceDetailsPanel
						data={instanceDetails}
						onClose={() => setSelectedInstanceId(null)}
					/>
				</CommonSideDialog>
			)}
		</div>
	);
};

export default AllServiceRequestInstancesPage;
